import React, { useState, useEffect } from 'react';
import './CurrencyConverterPage.css';

function CurrencyConverterPage() {
  const [amount, setAmount] = useState(1000);
  const [fromCurrency, setFromCurrency] = useState('VUV');
  const [toCurrency, setToCurrency] = useState('USD');
  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rates, setRates] = useState({});

  const currencies = [
    { code: 'VUV', name: 'Vanuatu Vatu', flag: '🇻🇺' },
    { code: 'USD', name: 'US Dollar', flag: '🇺🇸' },
    { code: 'AUD', name: 'Australian Dollar', flag: '🇦🇺' },
    { code: 'NZD', name: 'New Zealand Dollar', flag: '🇳🇿' },
    { code: 'EUR', name: 'Euro', flag: '🇪🇺' },
    { code: 'GBP', name: 'British Pound', flag: '🇬🇧' },
    { code: 'JPY', name: 'Japanese Yen', flag: '🇯🇵' },
    { code: 'CNY', name: 'Chinese Yuan', flag: '🇨🇳' },
  ];

  useEffect(() => {
    fetchRates();
  }, []);

  useEffect(() => {
    if (rates && Object.keys(rates).length > 0) {
      convertCurrency();
    }
  }, [amount, fromCurrency, toCurrency, rates]);

  const fetchRates = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Using exchangerate-api.com free tier
      const response = await fetch('https://api.exchangerate-api.com/v4/latest/VUV');
      
      if (!response.ok) throw new Error('Failed to fetch exchange rates');
      
      const data = await response.json();
      setRates(data.rates);
    } catch (err) {
      setError('Unable to fetch exchange rates. Please try again later.');
      console.error('Error fetching rates:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const convertCurrency = () => {
    if (!rates || Object.keys(rates).length === 0) return;

    let convertedAmount;
    
    if (fromCurrency === 'VUV') {
      // Converting from VUV to another currency
      convertedAmount = amount * (rates[toCurrency] || 1);
    } else if (toCurrency === 'VUV') {
      // Converting to VUV from another currency
      convertedAmount = amount / (rates[fromCurrency] || 1);
    } else {
      // Converting between two non-VUV currencies
      const toVUV = amount / (rates[fromCurrency] || 1);
      convertedAmount = toVUV * (rates[toCurrency] || 1);
    }

    setResult(convertedAmount);
  };

  const swapCurrencies = () => {
    setFromCurrency(toCurrency);
    setToCurrency(fromCurrency);
  };

  const formatNumber = (num) => {
    if (!num) return '0.00';
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  return (
    <div className="currency-converter-page">
      <div className="converter-header">
        <h1>💱 Currency Converter</h1>
        <p className="converter-subtitle">Convert Vanuatu Vatu to other currencies</p>
      </div>

      {error && (
        <div className="converter-error">
          <p>⚠️ {error}</p>
          <button onClick={fetchRates} className="retry-btn-small">Retry</button>
        </div>
      )}

      <div className="converter-container">
        <div className="converter-card">
          <div className="currency-input-group">
            <label>From</label>
            <div className="currency-select-wrapper">
              <select 
                value={fromCurrency} 
                onChange={(e) => setFromCurrency(e.target.value)}
                className="currency-select"
              >
                {currencies.map(curr => (
                  <option key={curr.code} value={curr.code}>
                    {curr.flag} {curr.code} - {curr.name}
                  </option>
                ))}
              </select>
            </div>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(parseFloat(e.target.value) || 0)}
              className="amount-input"
              placeholder="Enter amount"
            />
          </div>

          <button className="swap-btn" onClick={swapCurrencies} title="Swap currencies">
            ⇅
          </button>

          <div className="currency-input-group">
            <label>To</label>
            <div className="currency-select-wrapper">
              <select 
                value={toCurrency} 
                onChange={(e) => setToCurrency(e.target.value)}
                className="currency-select"
              >
                {currencies.map(curr => (
                  <option key={curr.code} value={curr.code}>
                    {curr.flag} {curr.code} - {curr.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="result-display">
              {isLoading ? (
                <div className="result-loading">Loading...</div>
              ) : (
                <div className="result-value">
                  {formatNumber(result)}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="converter-info">
          <h3>Exchange Rate Information</h3>
          <div className="rate-info">
            <p>1 {fromCurrency} = {formatNumber(
              fromCurrency === 'VUV' 
                ? rates[toCurrency] 
                : fromCurrency === toCurrency 
                  ? 1 
                  : (rates[toCurrency] || 1) / (rates[fromCurrency] || 1)
            )} {toCurrency}</p>
            <p className="rate-disclaimer">
              Exchange rates are updated periodically. Actual rates may vary.
            </p>
          </div>
        </div>

        <div className="quick-amounts">
          <h3>Quick Convert</h3>
          <div className="quick-amounts-grid">
            {[1000, 5000, 10000, 50000, 100000].map(quickAmount => (
              <button
                key={quickAmount}
                onClick={() => setAmount(quickAmount)}
                className="quick-amount-btn"
              >
                {formatNumber(quickAmount)} {fromCurrency}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="converter-footer">
        <p>💡 Tip: Vanuatu Vatu (VUV) is the official currency of Vanuatu. Most businesses accept cash, and ATMs are available in major towns.</p>
      </div>
    </div>
  );
}

export default CurrencyConverterPage;
